import React, { useEffect, useState } from "react";
import { api, formatCurrency, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Save, Layers, Plus, Trash2, Star } from "lucide-react";

const empty = () => ({ name: "", label: "", price_monthly: 0, price_annual: 0, currency: "FCFA", max_users: 1, max_staff: 1, max_admins: 0, max_agencies: 1, max_tickets_per_month: 50, features: [], highlight: false, active: true });

export default function SuperAdminPlans() {
  const [plans, setPlans] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [newPlan, setNewPlan] = useState(empty());

  const load = () => api.get("/superadmin/plans").then((r) => setPlans(r.data));
  useEffect(() => { load(); }, []);

  const save = async (p) => {
    try {
      await api.patch(`/superadmin/plans/${p.id}`, {
        label: p.label,
        price_monthly: Number(p.price_monthly),
        price_annual: Number(p.price_annual),
        max_users: Number(p.max_users),
        max_staff: Number(p.max_staff),
        max_admins: Number(p.max_admins || 0),
        max_agencies: Number(p.max_agencies || 1),
        max_tickets_per_month: Number(p.max_tickets_per_month),
        features: p.features || [],
        highlight: !!p.highlight,
        active: p.active !== false,
      });
      toast.success(`Plan ${p.name} mis à jour`); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Supprimer le plan "${p.name}" ? Impossible si des entreprises l'utilisent.`)) return;
    try { await api.delete(`/superadmin/plans/${p.id}`); toast.success("Plan supprimé"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const create = async () => {
    if (!newPlan.name || !newPlan.label) { toast.error("Nom et libellé requis"); return; }
    try {
      await api.post("/superadmin/plans", {
        ...newPlan,
        price_monthly: Number(newPlan.price_monthly),
        price_annual: Number(newPlan.price_annual),
        max_users: Number(newPlan.max_users),
        max_staff: Number(newPlan.max_staff),
        max_admins: Number(newPlan.max_admins),
        max_agencies: Number(newPlan.max_agencies),
        max_tickets_per_month: Number(newPlan.max_tickets_per_month),
        features: newPlan.features.filter(Boolean),
      });
      toast.success("Plan créé"); setOpenNew(false); setNewPlan(empty()); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const update = (id, field, value) => setPlans((prev) => prev.map((p) => p.id === id ? { ...p, [field]: value } : p));

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold">Plans tarifaires</h1>
          <p className="text-sm text-slate-500">{plans.length} plans configurés — jamais codés en dur</p>
        </div>
        <Button className="btn-pill bg-slate-900 hover:bg-slate-800 text-white" onClick={() => setOpenNew(true)} data-testid="sa-plan-new-btn"><Plus className="w-4 h-4 mr-1" /> Nouveau plan</Button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {plans.map((p) => (
          <Card key={p.id} data-testid={`sa-plan-card-${p.name}`} className={p.highlight ? "border-orange-500 ring-1 ring-orange-500/30" : ""}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1">Plan {p.highlight && <Star className="w-3 h-3 text-orange-500" />}</div>
                <CardTitle className="font-display flex items-center gap-2"><Layers className="w-4 h-4 text-orange-500" /> {p.name}</CardTitle>
              </div>
              <div className="text-2xl font-display font-bold text-orange-500">{formatCurrency(p.price_monthly, p.currency)}</div>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Libellé</Label><Input value={p.label || ""} onChange={(e) => update(p.id, "label", e.target.value)} data-testid={`sa-plan-label-${p.name}`} /></div>
              <div><Label>Prix mensuel</Label><Input type="number" value={p.price_monthly} onChange={(e) => update(p.id, "price_monthly", e.target.value)} data-testid={`sa-plan-monthly-${p.name}`} /></div>
              <div><Label>Prix annuel</Label><Input type="number" value={p.price_annual} onChange={(e) => update(p.id, "price_annual", e.target.value)} data-testid={`sa-plan-annual-${p.name}`} /></div>
              <div><Label>Max Admin</Label><Input type="number" value={p.max_admins} onChange={(e) => update(p.id, "max_admins", e.target.value)} data-testid={`sa-plan-admins-${p.name}`} /></div>
              <div><Label>Max Staff</Label><Input type="number" value={p.max_staff} onChange={(e) => update(p.id, "max_staff", e.target.value)} data-testid={`sa-plan-staff-${p.name}`} /></div>
              <div><Label>Max agences</Label><Input type="number" value={p.max_agencies} onChange={(e) => update(p.id, "max_agencies", e.target.value)} data-testid={`sa-plan-agencies-${p.name}`} /></div>
              <div><Label>Billets/mois</Label><Input type="number" value={p.max_tickets_per_month} onChange={(e) => update(p.id, "max_tickets_per_month", e.target.value)} data-testid={`sa-plan-tickets-${p.name}`} /></div>
              <div className="col-span-2 flex items-center justify-between border-t border-slate-200 dark:border-slate-800 pt-3">
                <label className="flex items-center gap-2 text-sm"><Switch checked={!!p.highlight} onCheckedChange={(v) => update(p.id, "highlight", v)} data-testid={`sa-plan-highlight-${p.name}`} /> Mis en avant</label>
                <label className="flex items-center gap-2 text-sm"><Switch checked={p.active !== false} onCheckedChange={(v) => update(p.id, "active", v)} data-testid={`sa-plan-active-${p.name}`} /> Actif</label>
              </div>
              <Button className="col-span-2 btn-pill bg-slate-900 hover:bg-slate-800 text-white" onClick={() => save(p)} data-testid={`sa-plan-save-${p.name}`}><Save className="w-4 h-4 mr-1" /> Enregistrer</Button>
              <Button className="col-span-2 btn-pill" variant="ghost" onClick={() => remove(p)} data-testid={`sa-plan-delete-${p.name}`}><Trash2 className="w-4 h-4 mr-1 text-red-500" /> Supprimer</Button>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={openNew} onOpenChange={setOpenNew}>
        <DialogContent data-testid="sa-plan-new-dialog">
          <DialogHeader><DialogTitle>Créer un nouveau plan</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Nom (ex: ENTERPRISE)</Label><Input value={newPlan.name} onChange={(e) => setNewPlan({ ...newPlan, name: e.target.value.toUpperCase() })} data-testid="sa-plan-new-name" /></div>
            <div><Label>Libellé</Label><Input value={newPlan.label} onChange={(e) => setNewPlan({ ...newPlan, label: e.target.value })} data-testid="sa-plan-new-label" /></div>
            <div><Label>Prix mensuel</Label><Input type="number" value={newPlan.price_monthly} onChange={(e) => setNewPlan({ ...newPlan, price_monthly: e.target.value })} /></div>
            <div><Label>Prix annuel</Label><Input type="number" value={newPlan.price_annual} onChange={(e) => setNewPlan({ ...newPlan, price_annual: e.target.value })} /></div>
            <div><Label>Max Staff</Label><Input type="number" value={newPlan.max_staff} onChange={(e) => setNewPlan({ ...newPlan, max_staff: e.target.value })} /></div>
            <div><Label>Max Admin</Label><Input type="number" value={newPlan.max_admins} onChange={(e) => setNewPlan({ ...newPlan, max_admins: e.target.value })} /></div>
            <div><Label>Max agences</Label><Input type="number" value={newPlan.max_agencies} onChange={(e) => setNewPlan({ ...newPlan, max_agencies: e.target.value })} /></div>
            <div><Label>Billets/mois</Label><Input type="number" value={newPlan.max_tickets_per_month} onChange={(e) => setNewPlan({ ...newPlan, max_tickets_per_month: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenNew(false)}>Annuler</Button>
            <Button className="bg-orange-500 hover:bg-orange-600 text-white" onClick={create} data-testid="sa-plan-new-submit">Créer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
